import React, { Component } from 'react'

/*
 * MathText — renders plain-text game content with the mathematical parts
 * typeset by MathJax.
 *
 * Game content is stored as plain text, so this component:
 *   1. finds the math-looking runs in the string (numbers, variables,
 *      operators, f(x), x^2, x/2, √x, 50%, 90°, ...),
 *   2. converts each run to TeX (x/2 -> \frac{x}{2}, x^2 -> x^{2}, * -> \cdot),
 *   3. converts each run straight to MathJax output with tex2svg. Anything
 *      MathJax can't parse stays as the original plain text.
 *
 * Why direct conversion instead of MathJaxManager.typeset()? The quizzes put
 * \( \) into the page and let MathJax scan for it, which works because each
 * quiz runs in its own iframe with a MathJax that Edviz configured. The games
 * run in the main app window, which can already have a MathJax with different
 * settings (e.g. only $...$ delimiters); MathJaxManager reuses that copy and
 * the scan then leaves "\(100\)" on screen. Converting each run directly works
 * with any MathJax 3 copy, whatever its delimiter settings.
 *
 * If MathJax fails to load, or a string contains no math, the original
 * plain text is shown.
 */

// ─────────────────────────── MathJax loader ───────────────────────────
// Same CDN + config Edviz uses (MathJax 3, tex-svg).
const MATHJAX_CDN = 'https://cdn.jsdelivr.net/npm/mathjax@3/es5/tex-svg.js'
let mathJaxPromise = null

export const loadMathJax = () => {
    if (mathJaxPromise) return mathJaxPromise
    mathJaxPromise = new Promise((resolve, reject) => {
        if (typeof window === 'undefined') return reject(new Error('MathJax needs a browser'))

        const done = () => window.MathJax.startup.promise.then(() => resolve(window.MathJax), reject)

        // Already loaded by anyone (Edviz, the app, another component)
        if (window.MathJax && window.MathJax.startup && window.MathJax.startup.promise) return done()

        // A MathJax script is already on the page but still loading: wait for it
        let script = document.getElementById('MathJax-script') ||
            document.querySelector('script[src*="mathjax"]')

        if (!script) {
            window.MathJax = {
                tex: { inlineMath: [['\\(', '\\)']], displayMath: [['\\[', '\\]']] },
                svg: { fontCache: 'global' },
            }
            script = document.createElement('script')
            script.id = 'MathJax-script'
            script.async = true
            script.src = MATHJAX_CDN
            document.head.appendChild(script)
        }

        script.addEventListener('load', done)
        script.addEventListener('error', () => {
            mathJaxPromise = null // allow a retry later
            reject(new Error('Failed to load MathJax'))
        })
    })
    return mathJaxPromise
}

// Same rule the quiz iframes use: MathJax output shouldn't swallow clicks /
// taps meant for the answer card, and shouldn't open MathJax's context menu.
// ── Look of the math ──
// MATH_SCALE: size of the math relative to the surrounding text (1 = same).
// MATH_WEIGHT: extra glyph thickness. MathJax draws math as SVG paths, so CSS
// font-weight has no effect; a thin outline stroke thickens it instead.
// 3 = MathJax default, ~20 = slightly bolder, ~40 = noticeably bold.
export const MATH_SCALE = 1.2
export const MATH_WEIGHT = 20

let styleInjected = false
const injectStyle = () => {
    if (styleInjected || typeof document === 'undefined') return
    styleInjected = true
    const style = document.createElement('style')
    style.textContent = `
        .game-math-text mjx-container,
        .game-math-text mjx-container * {
            pointer-events: none !important;
        }
        .game-math-text mjx-container {
            font-size: ${MATH_SCALE * 100}% !important;
        }
        .game-math-text mjx-container[jax="SVG"] {
            display: inline-block;
            margin: 0 !important;
        }
        .game-math-text mjx-container[jax="SVG"] use[data-c],
        .game-math-text mjx-container[jax="SVG"] path[data-c] {
            stroke-width: ${MATH_WEIGHT} !important;
        }
        .game-math-text mjx-container[jax="CHTML"] {
            -webkit-text-stroke: ${MATH_WEIGHT / 1000}em currentColor;
        }
    `
    document.head.appendChild(style)
}

// ─────────────────────────── Tokenizer ───────────────────────────

// Order matters: longer / more specific patterns first.
const TOKEN_RE = new RegExp([
    '(\\s+)',                                                // 1 space
    '(\\d{1,3}(?:,\\d{3})+(?:\\.\\d+)?(?![\\d,]?\\d)|\\d+\\.\\d+|\\.\\d+|\\d+)', // 2 number
    "([A-Za-z]+(?:['’][A-Za-z]+)*)",                          // 3 word (keeps don't, x's together)
    '(<=|>=|!=|[+\\-−–*×÷·/=<>≤≥≠≈±^_()\\[\\]√π°%$,²³])',     // 4 operator
    '([\\s\\S])',                                            // 5 anything else (breaks math)
].join('|'), 'g')

const tokenize = (text) => {
    const tokens = []
    let m
    TOKEN_RE.lastIndex = 0
    while ((m = TOKEN_RE.exec(text)) !== null) {
        const start = m.index
        const v = m[0]
        if (m[1] !== undefined) tokens.push({ t: 'space', v, start })
        else if (m[2] !== undefined) tokens.push({ t: 'num', v, start })
        else if (m[3] !== undefined) tokens.push({ t: 'word', v, start })
        else if (m[4] !== undefined) tokens.push({ t: 'op', v, start })
        else tokens.push({ t: 'other', v, start })
    }
    return tokens
}

const FUNCS = { sin: '\\sin', cos: '\\cos', tan: '\\tan', log: '\\log', ln: '\\ln', sqrt: 'sqrt' }
// Letters glued to a number that are NOT variables (ordinals, units, "1970s")
const ATTACHED_NOT_VARS = new Set([
    'st', 'nd', 'rd', 'th', 's',
    'cm', 'mm', 'km', 'kg', 'mg', 'lb', 'lbs', 'oz', 'ft', 'in', 'yd', 'mi', 'ml', 'hr', 'hrs',
    'am', 'pm', 'min', 'sec', 'mph',
])
const PREFIX_OPS = new Set(['(', '[', '-', '−', '–', '+', '$', '√', '±'])
const POSTFIX_OPS = new Set([')', ']', '%', '°', '²', '³'])

// Short words that are English, never a product of variables like "mx" or "lw"
const ENGLISH_SHORT = new Set(('ok hi in is or an as at be by do go he if it me my no of on so to up us we am ' +
    'and the for are but not you all any can her was one our out his has its who how may now see two way ' +
    'use few per let say set get put yes ten six off old new big top end add sum').split(' '))
const MATH_OPS = new Set(['+', '-', '−', '–', '*', '×', '÷', '·', '/', '=', '<', '>', '<=', '>=', '!=', '≤', '≥', '≠', '≈', '±', '^'])

// Classify each token as an operand ('x'), operator ('op'), space or breaker.
const classify = (tokens, text) => {
    const prevSolid = (i) => { let j = i - 1; while (j >= 0 && tokens[j].t === 'space') j--; return tokens[j] }
    const nextSolid = (i) => { let j = i + 1; while (j < tokens.length && tokens[j].t === 'space') j++; return tokens[j] }
    return tokens.map((tok, i) => {
        const prev = tokens[i - 1]
        const next = tokens[i + 1]
        if (tok.t === 'num') {
            // H2O, CO2, A1, Grade3: a number glued to letters is a label, not math
            if (prev && prev.t === 'word') return { ...tok, k: 'break' }
            return { ...tok, k: 'x' }
        }
        if (tok.t === 'op') return { ...tok, k: 'op' }
        if (tok.t === 'space') return { ...tok, k: 'space' }
        if (tok.t === 'word') {
            const w = tok.v
            const lower = w.toLowerCase()
            const glued = prev && prev.t === 'num' // e.g. 2x, 2nd, 5cm, 1970s
            if (glued && ATTACHED_NOT_VARS.has(w)) return { ...tok, k: 'break' }
            if (next && next.t === 'num') return { ...tok, k: 'break' } // H2O, A1
            if (w.length === 1) {
                // "4 x 3" means 4 × 3
                const ps = prevSolid(i), ns = nextSolid(i)
                if (w === 'x' && !glued && prev && prev.t === 'space' && next && next.t === 'space' &&
                    ps && ps.t === 'num' && ns && ns.t === 'num') {
                    return { ...tok, t: 'op', v: '×', k: 'op' }
                }
                return { ...tok, k: 'x', letter: true }
            }
            if (lower === 'pi') return { ...tok, k: 'x', pi: true }
            // sin(x), log 5, sqrt(x)
            if (FUNCS[lower] && next && (next.v === '(' || (next.t === 'space' && tokens[i + 2] &&
                (tokens[i + 2].t === 'num' || (tokens[i + 2].t === 'word' && tokens[i + 2].v.length === 1))))) {
                return { ...tok, k: 'func', fn: FUNCS[lower] }
            }
            // 2xy, 3ab — letters glued to a number are variables
            if (glued && w.length <= 2 && /^[a-z]+$/i.test(w)) {
                return { ...tok, k: 'x', letters: true }
            }
            // y = mx + b, A = lw, P = 2lw: a short non-English word sitting
            // between math operators (or an operator and the end) is a product
            if (w.length <= 3 && !ENGLISH_SHORT.has(lower) && /^[a-zA-Z]+$/.test(w)) {
                const ps = prevSolid(i), ns = nextSolid(i)
                const opBefore = ps && ps.t === 'op' && MATH_OPS.has(ps.v)
                const opAfter = ns && ns.t === 'op' && MATH_OPS.has(ns.v)
                const ok = w.length === 2 ? (opBefore || opAfter) : (opBefore && opAfter)
                if (ok) return { ...tok, k: 'x', letters: true }
            }
            return { ...tok, k: 'break' }
        }
        return { ...tok, k: 'break' }
    })
}

const isOperandLike = (tok) => tok && (tok.k === 'x' || tok.k === 'func' ||
    (tok.k === 'op' && (tok.v === '(' || tok.v === '[' || tok.v === '√' || tok.v === '$')))
const isOperandEnd = (tok) => tok && (tok.k === 'x' ||
    (tok.k === 'op' && POSTFIX_OPS.has(tok.v)))

// Split the classified tokens into candidate runs of math.
const findRuns = (toks) => {
    const runs = []
    let cur = []
    const flush = () => { if (cur.length) runs.push(cur); cur = [] }
    let depth = 0
    for (let i = 0; i < toks.length; i++) {
        const tok = toks[i]
        if (tok.k === 'break') { flush(); depth = 0; continue }
        if (tok.k === 'op') {
            if (tok.v === '(' || tok.v === '[') depth++
            if (tok.v === ')' || tok.v === ']') depth = Math.max(0, depth - 1)
            // A comma only belongs to math inside brackets: (2, 3). Elsewhere it's prose.
            if (tok.v === ',' && depth === 0) { flush(); continue }
        }
        if (tok.k === 'space') {
            // Whitespace only joins math when an operator sits on one side:
            // "x + 1" joins, "5 apples" / "x y" / "2 x" split.
            let p = cur.length - 1
            while (p >= 0 && cur[p].k === 'space') p--
            const before = p >= 0 ? cur[p] : null
            let n = i + 1
            while (n < toks.length && toks[n].k === 'space') n++
            const after = toks[n]
            if (before && isOperandEnd(before) && isOperandLike(after) &&
                !(after.k === 'op' && after.v === '$')) {
                // "f(x) = 2" style spaces always sit next to an operator, so
                // operand <space> operand means two separate things.
                flush(); depth = 0; continue
            }
        }
        cur.push(tok)
    }
    flush()
    return runs
}

// Trim spaces and dangling operators off the ends of a run.
const trimRun = (run) => {
    let s = 0, e = run.length - 1
    let changed = true
    while (changed && s <= e) {
        changed = false
        while (s <= e && run[s].k === 'space') { s++; changed = true }
        while (s <= e && run[e].k === 'space') { e--; changed = true }
        if (s <= e && run[s].k === 'op' && !PREFIX_OPS.has(run[s].v)) { s++; changed = true }
        if (s <= e && run[e].k === 'op' && !POSTFIX_OPS.has(run[e].v)) { e--; changed = true }
        if (s <= e && run[e].k === 'func') { e--; changed = true }
        // A prefix op at the very end, or postfix op at the very start, is dangling
        if (s <= e && run[e].k === 'op' && PREFIX_OPS.has(run[e].v) && !POSTFIX_OPS.has(run[e].v)) { e--; changed = true }
        if (s <= e && run[s].k === 'op' && POSTFIX_OPS.has(run[s].v) && !PREFIX_OPS.has(run[s].v)) { s++; changed = true }
    }
    return run.slice(s, e + 1)
}

const balanced = (run) => {
    const stack = []
    for (const tok of run) {
        if (tok.k !== 'op') continue
        if (tok.v === '(' || tok.v === '[') stack.push(tok.v)
        else if (tok.v === ')') { if (stack.pop() !== '(') return false }
        else if (tok.v === ']') { if (stack.pop() !== '[') return false }
    }
    return stack.length === 0
}

// "(see x" -> "x", "x)" -> "x"; anything still unbalanced isn't math.
const fixBrackets = (run) => {
    let r = run
    for (let guard = 0; guard < 6 && r.length && !balanced(r); guard++) {
        const first = r[0], last = r[r.length - 1]
        const opens = r.filter(t => t.k === 'op' && (t.v === '(' || t.v === '[')).length
        const closes = r.filter(t => t.k === 'op' && (t.v === ')' || t.v === ']')).length
        if (opens > closes && first.k === 'op' && (first.v === '(' || first.v === '[')) r = trimRun(r.slice(1))
        else if (closes > opens && last.k === 'op' && (last.v === ')' || last.v === ']')) r = trimRun(r.slice(0, -1))
        else return null
    }
    return r.length && balanced(r) ? r : null
}

// ─────────────────────────── Run -> TeX ───────────────────────────

const OP_TEX = {
    '+': '+', '-': '-', '−': '-', '–': '-', '*': '\\cdot', '·': '\\cdot', '×': '\\times', '÷': '\\div',
    '=': '=', '<': '\\lt', '>': '\\gt', '<=': '\\le', '>=': '\\ge', '!=': '\\ne',
    '≤': '\\le', '≥': '\\ge', '≠': '\\ne', '≈': '\\approx', '±': '\\pm', ',': ',',
}

class TexParseError extends Error { }

const numTex = (v) => v.replace(/,/g, '{,}')

// Turns a run (no spaces) into TeX. Throws TexParseError when unsure.
const runToTex = (toks) => {
    let i = 0
    const peek = () => toks[i]

    // primary := number | letter | pi | (seq) | [seq] | √primary | func primary
    const parsePrimary = () => {
        const tok = peek()
        if (!tok) throw new TexParseError('expected operand')
        if (tok.k === 'x') {
            i++
            if (tok.t === 'num') return { tex: numTex(tok.v) }
            if (tok.pi) return { tex: '\\pi' }
            // f(x), g(2): keep the function and its argument together as one atom
            if (tok.letter && peek() && peek().v === '(' && peek().start === tok.start + 1) {
                const group = parseGroup()
                return { tex: tok.v + group.tex }
            }
            return { tex: tok.v }
        }
        if (tok.k === 'op' && tok.v === 'π') { i++; return { tex: '\\pi' } }
        if (tok.k === 'op' && tok.v === '$') {
            i++
            const n = peek()
            if (!n || n.t !== 'num') throw new TexParseError('$ without number')
            i++
            return { tex: '\\$' + numTex(n.v) }
        }
        if (tok.k === 'op' && (tok.v === '(' || tok.v === '[')) return parseGroup()
        if (tok.k === 'op' && tok.v === '√') {
            i++
            const arg = parseScriptArg()
            return { tex: `\\sqrt{${arg}}` }
        }
        if (tok.k === 'func') {
            i++
            if (tok.fn === 'sqrt') {
                const arg = parseScriptArg()
                return { tex: `\\sqrt{${arg}}` }
            }
            const arg = parsePostfixed()
            return { tex: `${tok.fn}${/^(\\left)?[(\[]/.test(arg.tex) ? '' : ' '}${arg.tex}` }
        }
        throw new TexParseError('unexpected ' + tok.v)
    }

    // ( seq ) -> { tex: '\left(...\right)', inner: '...' }
    const parseGroup = () => {
        const open = peek().v
        const close = open === '(' ? ')' : ']'
        i++
        const inner = parseSeq(close)
        if (!peek() || peek().v !== close) throw new TexParseError('unclosed group')
        i++
        // Only stretch brackets around tall content; plain ( ) keeps f(x) tight
        const tall = /\\frac|\\sqrt|\^/.test(inner)
        const l = tall ? (open === '(' ? '\\left(' : '\\left[') : open
        const r = tall ? (close === ')' ? '\\right)' : '\\right]') : close
        return { tex: `${l}${inner}${r}`, inner }
    }

    // argument of ^, _ and √: (group) -> its contents, -primary, or primary
    const parseScriptArg = () => {
        const tok = peek()
        if (!tok) throw new TexParseError('missing script argument')
        if (tok.k === 'op' && (tok.v === '-' || tok.v === '−' || tok.v === '–')) {
            i++
            return '-' + parseScriptArg()
        }
        if (tok.k === 'op' && tok.v === '(') {
            const g = parseGroup()
            return g.inner
        }
        // single number / letter only — "x^2y" is x^{2}y, not x^{2y}
        if (tok.k === 'x' && (tok.t === 'num' || tok.letter || tok.pi)) {
            i++
            return tok.t === 'num' ? numTex(tok.v) : tok.pi ? '\\pi' : tok.v
        }
        throw new TexParseError('bad script argument')
    }

    // primary followed by ^, _, %, °, ², ³
    const parsePostfixed = () => {
        const atom = parsePrimary()
        let tex = atom.tex
        let inner = atom.inner
        for (; ;) {
            const tok = peek()
            if (!tok || tok.k !== 'op') break
            if (tok.v === '^' || tok.v === '_') {
                i++
                tex = `{${tex}}${tok.v}{${parseScriptArg()}}`
                inner = undefined
            } else if (tok.v === '²' || tok.v === '³') {
                i++
                tex = `{${tex}}^{${tok.v === '²' ? 2 : 3}}`
                inner = undefined
            } else if (tok.v === '%') {
                i++; tex += '\\%'; inner = undefined
            } else if (tok.v === '°') {
                i++; tex += '^{\\circ}'; inner = undefined
            } else break
        }
        return { tex, inner }
    }

    // seq := (atom | op)* ; then simple a/b -> \frac{a}{b}
    const parseSeq = (until) => {
        const items = []
        while (peek() && !(until && peek().v === until)) {
            const tok = peek()
            if (tok.k === 'op' && (OP_TEX[tok.v] !== undefined || tok.v === '/')) {
                // Commas are only list separators inside brackets
                if (tok.v === ',' && !until) throw new TexParseError('comma outside brackets')
                i++
                items.push({ op: tok.v })
                continue
            }
            if (tok.k === 'op' && (tok.v === ')' || tok.v === ']')) throw new TexParseError('stray close')
            items.push({ atom: parsePostfixed() })
        }
        // fractions: atom / atom, but not chains like 10/20/2026
        const out = []
        for (let k = 0; k < items.length; k++) {
            const it = items[k]
            if (it.op === '/' && out.length && out[out.length - 1].atom && items[k + 1] && items[k + 1].atom &&
                !(items[k - 2] && items[k - 2].op === '/') && !(items[k + 2] && items[k + 2].op === '/')) {
                const a = out.pop().atom
                const b = items[k + 1].atom
                out.push({ atom: { tex: `\\frac{${a.inner !== undefined ? a.inner : a.tex}}{${b.inner !== undefined ? b.inner : b.tex}}` } })
                k++
                continue
            }
            out.push(it)
        }
        return out.map(it => it.atom ? it.atom.tex : (it.op === '/' ? '/' : OP_TEX[it.op])).join(' ')
    }

    const tex = parseSeq(null)
    if (i !== toks.length) throw new TexParseError('trailing tokens')
    return tex
}

// Is this run worth typesetting?
const isMathRun = (run, text) => {
    const operands = run.filter(t => t.k === 'x')
    if (operands.length === 0) return false
    const realOps = run.filter(t => t.k === 'op' && !['(', ')', '[', ']'].includes(t.v))
    // A lone letter: "solve for x" yes; "a", "I", "A", "(b)", "e.g." no
    const hasFunc = run.some(t => t.k === 'func')
    if (operands.length === 1 && operands[0].letter && realOps.length === 0 && !hasFunc) {
        const tok = operands[0]
        if (run.length !== 1) return false
        if (!/^[a-z]$/.test(tok.v) || tok.v === 'a' || tok.v === 'i') return false
        const after = text.slice(tok.start + 1, tok.start + 3)
        const before = text.slice(Math.max(0, tok.start - 2), tok.start)
        if (/^\.[A-Za-z]/.test(after) || /[A-Za-z]\.$/.test(before)) return false
        return true
    }
    return true
}

/**
 * Split plain text into [{ type: 'text' | 'math', value, tex? }].
 * Exported so it can be unit-tested or reused.
 */
export const toMathSegments = (text) => {
    if (text === null || text === undefined) return []
    const str = String(text)
    if (!str) return []
    let toks
    try {
        toks = classify(tokenize(str), str)
    } catch (e) {
        return [{ type: 'text', value: str }]
    }
    const pieces = []
    for (const raw of findRuns(toks)) {
        let run = trimRun(raw)
        if (!run.length) continue
        run = fixBrackets(run)
        if (!run || !isMathRun(run, str)) continue
        let tex
        try {
            tex = runToTex(run.filter(t => t.k !== 'space'))
        } catch (e) {
            continue // too hard to convert: leave as plain text
        }
        const first = run[0], last = run[run.length - 1]
        pieces.push({ start: first.start, end: last.start + last.v.length, tex })
    }
    const segments = []
    let pos = 0
    for (const p of pieces) {
        if (p.start > pos) segments.push({ type: 'text', value: str.slice(pos, p.start) })
        segments.push({ type: 'math', value: str.slice(p.start, p.end), tex: p.tex })
        pos = p.end
    }
    if (pos < str.length) segments.push({ type: 'text', value: str.slice(pos) })
    return segments
}

/**
 * Whether a game's content should get math typesetting. Math and science get
 * it; ELA and social studies stay plain text so years ("In 1776") and other
 * numbers in prose aren't typeset. Unknown subjects default to math on.
 */
export const shouldRenderMath = (gameData) => {
    const std = gameData && gameData.standard
    const category = (std && std.standardCategory) || (gameData && gameData.standardCategory) || ''
    return !/\b(ela|english|reading|writing|language arts|social studies|history|civics)\b/i.test(String(category))
}

// ─────────────────────────── React component ───────────────────────────

const escapeHtml = (s) => String(s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')

// TeX -> MathJax output node, or null if MathJax can't parse it.
const texToNode = async (MathJax, tex) => {
    const opts = { display: false }
    let node = null
    try {
        if (MathJax.tex2svgPromise) node = await MathJax.tex2svgPromise(tex, opts)
        else if (MathJax.tex2chtmlPromise) node = await MathJax.tex2chtmlPromise(tex, opts)
        else if (MathJax.tex2svg) node = MathJax.tex2svg(tex, opts)
        else if (MathJax.tex2chtml) node = MathJax.tex2chtml(tex, opts)
    } catch (e) {
        return null
    }
    // TeX errors come back as an <merror> node rather than being thrown
    if (!node || node.querySelector('[data-mml-node="merror"], mjx-merror')) return null
    return node
}

let warnedNoTex = false
const canConvert = (MathJax) => {
    const ok = !!(MathJax && (MathJax.tex2svgPromise || MathJax.tex2chtmlPromise || MathJax.tex2svg || MathJax.tex2chtml))
    if (!ok && !warnedNoTex) {
        warnedNoTex = true
        console.warn('Games: the MathJax on this page has no TeX input (version ' +
            (MathJax && MathJax.version) + '), showing math as plain text.')
    }
    return ok
}

// CHTML output keeps its glyph CSS in a stylesheet that has to be refreshed
// after converting outside of typeset(). SVG output (what Edviz loads) needs
// nothing: its global font cache is added to the page automatically.
let chtmlRefresh = null
const refreshChtmlStyles = (MathJax) => {
    if (!MathJax.tex2chtml || MathJax.tex2svg || !MathJax.startup || !MathJax.startup.document) return
    if (chtmlRefresh) return
    chtmlRefresh = setTimeout(() => {
        chtmlRefresh = null
        try {
            MathJax.startup.document.clear()
            MathJax.startup.document.updateDocument()
        } catch (e) { /* ignore */ }
    }, 0)
}

export class MathText extends Component {
    el = null
    renderId = 0

    componentDidMount() {
        injectStyle()
        this.typeset()
    }

    componentDidUpdate(prevProps) {
        if (prevProps.text !== this.props.text) this.typeset()
    }

    componentWillUnmount() {
        this.unmounted = true
    }

    typeset = () => {
        const id = ++this.renderId
        const segments = toMathSegments(this.props.text)
        if (!segments.some(s => s.type === 'math')) return
        const current = () => !this.unmounted && id === this.renderId && this.el
        loadMathJax().then(async (MathJax) => {
            if (!current() || !canConvert(MathJax)) return
            // An answer that is just "(x+1)/2" gets a full-size fraction
            const whole = segments.length === 1
            const nodes = await Promise.all(segments.map(seg => seg.type === 'math'
                ? texToNode(MathJax, whole ? seg.tex.replace(/\\frac\{/g, '\\dfrac{') : seg.tex)
                : null))
            if (!current()) return
            const frag = document.createDocumentFragment()
            segments.forEach((seg, i) => {
                if (nodes[i]) {
                    const span = document.createElement('span')
                    span.className = 'game-math'
                    span.appendChild(nodes[i])
                    frag.appendChild(span)
                } else {
                    frag.appendChild(document.createTextNode(seg.value))
                }
            })
            this.el.innerHTML = ''
            this.el.appendChild(frag)
            refreshChtmlStyles(MathJax)
        }).catch((err) => {
            console.warn('Games: MathJax unavailable, showing plain text.', err)
        })
    }

    render() {
        const { text, style, className } = this.props
        // Plain text is rendered first (and is the fallback). React only
        // rewrites innerHTML when the text itself changes, so MathJax's output
        // survives re-renders such as the answer feedback state.
        return (
            <span
                ref={(el) => { this.el = el }}
                className={'game-math-text' + (className ? ' ' + className : '')}
                style={style}
                dangerouslySetInnerHTML={{ __html: escapeHtml(text === null || text === undefined ? '' : text) }}
            />
        )
    }
}

export default MathText